/**
 * TEMPORARY: Firebase to Supabase migration check hook
 * @see MIGRATION-REMOVAL.md for removal instructions
 *
 * This hook detects users who have an active Firebase session but no Supabase session,
 * indicating they need to be migrated. It triggers a modal prompting them to enter
 * their password to complete the migration seamlessly.
 */

import type { User as FirebaseUser } from 'firebase/auth';
import { onAuthStateChanged } from 'firebase/auth';
import { useCallback, useEffect, useState } from 'react';

import { getFirebaseAuth } from '@/lib/firebase';
import { supabase } from '@/lib/supabase';

interface MigrationState {
	/** Whether the user needs to migrate from Firebase to Supabase */
	needsMigration: boolean;
	/** The Firebase user object (if they have a Firebase session) */
	firebaseUser: FirebaseUser | null;
	/** Whether we're still checking auth state */
	isChecking: boolean;
}

/**
 * Hook to check if a user needs to be migrated from Firebase to Supabase
 * TEMPORARY: Remove after all users have been migrated
 *
 * @returns Migration state and controls
 */
export const useMigrationCheck = () => {
	const [state, setState] = useState<MigrationState>({
		needsMigration: false,
		firebaseUser: null,
		isChecking: true,
	});

	useEffect(() => {
		const auth = getFirebaseAuth();

		const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
			if (fbUser) {
				// Firebase user exists - check if they have a Supabase session
				try {
					const {
						data: { session },
					} = await supabase.auth.getSession();

					if (!session) {
						// Has Firebase session but no Supabase session - needs migration
						setState({
							needsMigration: true,
							firebaseUser: fbUser,
							isChecking: false,
						});
						return;
					}
				} catch (error) {
					console.error('Error checking Supabase session:', error);
				}
			}

			// Either no Firebase user, or already has Supabase session
			setState({
				needsMigration: false,
				firebaseUser: null,
				isChecking: false,
			});
		});

		return () => unsubscribe();
	}, []);

	/**
	 * Call this after successful migration to clear the migration state
	 */
	const onMigrationComplete = useCallback(() => {
		setState({
			needsMigration: false,
			firebaseUser: null,
			isChecking: false,
		});
	}, []);

	return {
		...state,
		onMigrationComplete,
	};
};
